/** Every lineup view, across both event types. */
export const LINEUP_VIEWS = [
  'programs',
  'timetable',
  'speakers',
  'partners',
  'artists',
] as const;

export type LineupView = (typeof LINEUP_VIEWS)[number];

/** Tab label per view. Shared by both lineups, which offer the same views. */
export const LINEUP_VIEW_LABELS: Record<LineupView, string> = {
  programs: 'lineup.tabs.programs',
  timetable: 'lineup.tabs.timetable',
  speakers: 'lineup.tabs.speakers',
  partners: 'lineup.tabs.partners',
  artists: 'lineup.tabs.artists',
};

/** An XP edition runs more than one programme, and the kind picks the views. */
export const LINEUP_KINDS = ['day', 'nite'] as const;

export type LineupKind = (typeof LINEUP_KINDS)[number];

/** Which lineup an event runs. The one decision the routing turns on. */
export type LineupType = 'xp' | 'standard';

/** XP day: sessions across rooms, with the people and partners behind them. */
export const XP_DAY_VIEWS: Array<LineupView> = [
  'programs',
  'timetable',
  'speakers',
  'partners',
];

/** XP nite: a programme of sets, so it opens on the programme like day does. */
export const XP_NITE_VIEWS: Array<LineupView> = [
  'programs',
  'artists',
  'timetable',
];

/** Standard (SS) events show artists alongside the timetable. */
export const STANDARD_LINEUP_VIEWS: Array<LineupView> = [
  'artists',
  'timetable',
];

/** What groups the sessions in the Programs view. */
export const PROGRAM_SORTS = ['date', 'stage', 'genre'] as const;

export type ProgramSort = (typeof PROGRAM_SORTS)[number];

export const PROGRAM_SORT_LABELS: Record<ProgramSort, string> = {
  date: 'lineup.programs.sort.date',
  stage: 'lineup.programs.sort.stage',
  genre: 'lineup.programs.sort.genre',
};

/** Genre is nite only: a day session carries none. */
export const getProgramSorts = (kind: LineupKind | undefined) =>
  PROGRAM_SORTS.filter((sort) => sort !== 'genre' || kind === 'nite');

/**
 * XP brands its stages in orange. Without this they fall back to the site
 * secondary, which reads as a different stage rather than an unset one.
 */
export const XP_STAGE_COLOR = '#EC7029';

/** Richest first: the array is the ranking, not a lookup. */
export const PARTNER_TIERS = ['golden', 'silver', 'bronze'] as const;

export type PartnerTier = (typeof PARTNER_TIERS)[number];

export const PARTNER_TIER_LABELS: Record<PartnerTier, string> = {
  golden: 'lineup.partners.tier.golden',
  silver: 'lineup.partners.tier.silver',
  bronze: 'lineup.partners.tier.bronze',
};

/**
 * A param rather than a path segment, so opening a detail is a shallow
 * navigation that leaves the list mounted underneath.
 */
export const LINEUP_ENTRY_PARAM = 'details';

/** Rides alongside the entry, so a b2b can open one of its artists. */
export const LINEUP_ARTIST_PARAM = 'artist';
